/**
 * Générateur — Il utilise des puissances de 10 pour comparer des nombres.
 * Attendu ID : 142 | Niveau : 4ème | Domaine : Nombres (desc 11, O7)
 *
 * Doc prof MUET en exercices (seule l'image « Exemples de réussite » p.12 :
 * « compare des très grands ou très petits nombres positifs en utilisant
 * l'écriture scientifique »). → BANQUE CRÉÉE, comme O18/O19 en divisibilité.
 * Plan de cartes validé 2026-07-14 :
 *
 *   Carte « comparer » — Compare deux nombres a × 10ⁿ (qcm_unique : <, >, =)
 *     inclut volontairement des cas d'ÉGALITÉ (écritures différentes, même valeur) ;
 *     le décalage d'écriture de B est aussi appliqué une fois sur deux hors égalité.
 *   Carte « plusgrand » — Le plus grand parmi 3-4 nombres a × 10ⁿ (qcm_unique)
 *   Carte « ranger » — Ranger dans l'ordre croissant (ordonner)
 *   Carte « encadrer » — Encadrer un nombre par deux puissances de 10 (saisie 2 trous)
 *     exposant [-4 ; 6], mantisse à 4 décimales au plus.
 *
 * Revue 2026-09-22 : « comparer » avait une fuite structurelle (« = » sortait
 * exactement quand B n'était pas normalisé, un quart des tirages) : le décalage
 * s'applique aussi une fois sur deux dans les cas < et >, la forme de B ne dit
 * plus rien ; « encadrer » [2 ; 7] → [-4 ; 6] (le nombre encadré était toujours
 * un entier ≥ 110, petits nombres et décimaux jamais posés), mantisse de 0 à 4
 * décimales, phrase du signe « - » ; parenthèses de consigne → phrases.
 *
 * Revue 2026-09-23 : tout ce qui s'affiche comme puissance passe en KaTeX
 * (règle de la maison, décision prof) : tex de generators/utils.js remplace
 * puiss de nombres.js (exposants Unicode) et les <sup> de l'énoncé, dans les
 * énoncés, les choix du QCM « plusgrand » et les étiquettes de « ranger » ; la
 * phrase à trous de « encadrer » passe en mode mathématique global (ligne
 * entière entre dollars), chaque exposant en trou protégé par un groupe,
 * 10^{{{p}}}. Les réponses (exposants saisis) ne changent pas.
 *
 * Variante difficile 2026-09-23 « exposant trompeur », un tirage sur cinq
 * (PART_EXPOSANT_TROMPEUR) sur comparer, plusgrand et ranger, jamais encadrer :
 * les écritures ne sont pas normalisées et le plus grand exposant AFFICHÉ n'est
 * pas celui du plus grand nombre (0,5 × 10⁶ < 8 × 10⁵). La règle du cours,
 * « le plus grand exposant gagne », n'est vraie qu'en écriture scientifique :
 * l'élève qui l'applique sans normaliser se trompe, et dans les deux sens (le
 * plus petit écrit avec un exposant plus grand, ou le plus grand écrit avec un
 * exposant plus petit). Domaine, borné : exposant vrai du plus grand nombre de
 * la paire piégée dans [-3 ; 6] ; son voisin au même exposant avec une mantisse
 * plus petite, ou un cran d'exposant en dessous ; décalage d'écriture de 1 ou
 * 2 crans (mantisse 0,c ou 0,0c, c0 ou c00) ; exposants affichés dans [-5 ; 8],
 * inclus dans ce que les tirages ordinaires affichent déjà. Toujours un ordre
 * strict (jamais « = ») : une égalité n'a pas de plus grand nombre.
 * Marqueur de différenciation : contenu.difficulte = 'exposant_trompeur',
 * posé SEULEMENT sur les tirages difficiles. Il vit dans contenu parce que
 * contenu est persisté en base alors que _meta est effacé au POST (seul
 * generatorKey en survit), même canal que contenu.calculatrice (gen_150). Il
 * ne sert encore à rien côté interface : il est là pour la différenciation à
 * venir. Recopié dans _meta.params (harnais, dumps). Pour que la forme ne
 * trahisse pas le piège, une écriture non normalisée apparaît aussi hors
 * variante : comparer décalait déjà B une fois sur deux ; plusgrand et ranger
 * décalent désormais un nombre au hasard une fois sur deux (seule l'écriture
 * change, jamais les valeurs ni la réponse).
 *
 * Nombres POSITIFS uniquement (programme). Comparaison EXACTE par comparaison de
 * chaînes décimales (developpe, aucun flottant). Puissances en KaTeX à
 * l'affichage, exposants saisis en ASCII (« -3 ») dans les trous.
 *
 * ÉCHELLE DE DIFFICULTÉ DE CET ATTENDU (note prof 2026-09-23, pour la
 * différenciation à venir). Comparer deux nombres écrits avec des puissances de
 * 10 se décompose en trois gestes, du plus simple au plus coûteux :
 *
 *   1. Les deux nombres sont en écriture scientifique et leurs exposants
 *      DIFFÈRENT : il suffit de comparer les exposants. Le geste d'entrée.
 *   2. Les deux sont en écriture scientifique et leurs exposants sont ÉGAUX :
 *      il faut descendre comparer les mantisses.
 *   3. Au moins une écriture n'est PAS normalisée : l'exposant affiché ment, et
 *      comparer les exposants donne la mauvaise réponse. C'est le seul échelon
 *      codé aujourd'hui, sous le nom `exposant_trompeur`.
 *
 * Les échelons 1 et 2 n'existent pas encore comme tirages identifiables : la
 * carte les produit au hasard et ne les marque pas. Le jour où on différencie,
 * il faudra les nommer et les poser dans `contenu.difficulte` de la même façon,
 * par exemple `exposants_differents` et `exposants_egaux`, et le taux de chacun
 * deviendra un réglage. L'idée du prof : servir l'échelon 1 à l'élève qui
 * démarre, puis le 2, puis le 3 quand il est à l'aise.
 *
 * La convention du champ `difficulte` est écrite une seule fois, dans
 * docs/FORMATS_TANOED.md §0.8 : elle vaut pour tous les gens. En résumé, la
 * valeur NOMME LE GESTE et jamais un niveau numérique, elle vit dans `contenu`
 * parce que `_meta` est effacé au POST, et une carte qui n'a qu'un seul régime
 * ne pose rien du tout. Aucun module partagé : une constante pour le taux, une
 * chaîne pour le nom, une ligne pour la poser.
 */

import {
    randomChoice, randInt, tex,
    makeQcm, makeOrdonner, makeTexteTrou,
} from '../../../utils';
import { developpe } from './nombres';

const ATTENDU_ID = 142;

// ── Variante difficile « exposant trompeur » (2026-09-23) ─────────────────
// Part des tirages difficiles sur les cartes comparer, plusgrand et ranger : un
// tirage sur cinq. Le professeur change ce nombre ici et nulle part ailleurs
// (0 = jamais, 1 = toujours).
const PART_EXPOSANT_TROMPEUR = 1 / 5;
// Valeur du marqueur contenu.difficulte posé sur ces tirages (et eux seuls).
const DIFFICULTE_TROMPEUR = 'exposant_trompeur';
// Domaine du cas difficile : exposant VRAI du plus grand nombre de la paire
// piégée, et amplitudes admises du décalage d'écriture (1 cran : 0,c ou c0 ;
// 2 crans : 0,0c ou c00). Les exposants affichés restent dans [P_MIN - 2 ; P_MAX + 2].
const TROMPEUR_P_MIN = -3;
const TROMPEUR_P_MAX = 6;
const TROMPEUR_DECALAGES = [1, 2];

/**
 * Les échelons de cet attendu, DU PLUS SIMPLE AU PLUS COÛTEUX : l'ordre du
 * tableau EST l'ordre de la progression. Donnée pure, jamais lue à l'exécution,
 * sur le modèle de REGLES_DISTRACTEURS_QCM ; elle existe pour que la
 * différenciation à venir lise une liste au lieu de lire de la prose, et pour
 * qu'un test puisse vérifier que tout échelon `pose: true` est bien posé dans
 * contenu.difficulte. Convention du champ : docs/FORMATS_TANOED.md §0.8.
 *
 * `pose: false` = l'échelon SORT déjà des tirages, mais rien ne le marque, donc
 * on ne sait pas le retrouver. Le jour où on différencie, il faudra le poser et
 * son taux deviendra un réglage, comme PART_EXPOSANT_TROMPEUR ci-dessus.
 */
export const ECHELONS_DIFFICULTE = [
    {
        code: 'exposants_differents',
        pose: false,
        geste: 'les deux nombres sont en écriture scientifique et leurs exposants diffèrent : comparer les exposants suffit',
    },
    {
        code: 'exposants_egaux',
        pose: false,
        geste: 'les deux sont en écriture scientifique et leurs exposants sont égaux : il faut descendre comparer les mantisses',
    },
    {
        code: DIFFICULTE_TROMPEUR,
        pose: true,
        geste: 'au moins une écriture n\'est pas normalisée : le plus grand exposant affiché n\'est pas celui du plus grand nombre, comparer les exposants donne la mauvaise réponse',
    },
];

const L_COMPARER = 'Comparer deux nombres avec les puissances de 10';
const L_PLUSGRAND = 'Trouver le plus grand nombre';
const L_RANGER = 'Ranger des nombres en écriture scientifique';
const L_ENCADRER = 'Encadrer un nombre par deux puissances de 10';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Séparateur de milliers (affichage) : « 4500 » → « 4 500 ».
const grp = (s) => {
    const [ent, dec] = String(s).split(',');
    const g = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return dec !== undefined ? `${g},${dec}` : g;
};

// Mantisse « quelconque » à 1-3 chiffres significatifs (ent 1-9, 0-2 décimales).
const mantAlea = () => {
    const ent = randInt(1, 9);
    const nd = randInt(0, 2);
    let d = '';
    for (let i = 0; i < nd; i++) d += String(randInt(0, 9));
    d = d.replace(/0+$/, '');
    return d ? `${ent},${d}` : String(ent);
};

// Compare DEUX chaînes décimales françaises POSITIVES : renvoie -1, 0, 1.
const cmpDec = (x, y) => {
    const norm = (s) => {
        let [i, d = ''] = String(s).replace(/\s/g, '').split(',');
        i = i.replace(/^0+(?=\d)/, '');
        d = d.replace(/0+$/, '');
        return [i, d];
    };
    const [xi, xd] = norm(x); const [yi, yd] = norm(y);
    if (xi.length !== yi.length) return xi.length < yi.length ? -1 : 1;
    if (xi !== yi) return xi < yi ? -1 : 1;
    const L = Math.max(xd.length, yd.length);
    const xp = xd.padEnd(L, '0'); const yp = yd.padEnd(L, '0');
    return xp === yp ? 0 : (xp < yp ? -1 : 1);
};

// Valeur décimale exacte de « mant × 10^e » (chaîne).
const valOf = (mant, e) => developpe(mant, e);
// Décimal français en LaTeX : « 3,4 » → « 3{,}4 » (sinon KaTeX espace la
// virgule). Sur une écriture SANS séparateur de milliers : les espaces sont
// ignorés en mode mathématique, les milliers sont posés au rendu
// (utils/milliers.js, espacerMilliersTex).
const nbTex = (s) => String(s).replace(',', '{,}');
// Fragment LaTeX « a \times 10^{n} », à composer dans un tex().
const sciFrag = (mant, e) => `${nbTex(mant)} \\times 10^{${e}}`;
// Affichage « a × 10ⁿ » en KaTeX.
const sci = (mant, e) => tex(sciFrag(mant, e));

// Décalage d'écriture : même valeur, mantisse hors de [1 ; 10[ (3,4 × 10³ → 0,34 × 10⁴).
const decaler = (m, e) => {
    const shift = randomChoice([1, 2, -1, -2]);
    return [valOf(m, -shift), e + shift];
};

// ═════════ Variante difficile : la paire à exposant trompeur ═════════

const tirageTrompeur = () => Math.random() < PART_EXPOSANT_TROMPEUR;

// Deux nombres { m, e } tels qu'AFFICHÉS, dont l'ordre des exposants affichés
// contredit l'ordre des valeurs. `grand` vaut a × 10^p (a normalisée ; le plus
// grand est donné, ou tiré dans le domaine) ; `petit` est son voisin : même
// exposant avec une mantisse plus petite, ou un cran d'exposant en dessous.
// Puis un décalage d'écriture de d crans, dans un sens sur deux :
//   'haut' : le petit s'écrit avec un exposant plus grand (0,5 × 10^6 contre 8 × 10^5) ;
//   'bas'  : le grand s'écrit avec un exposant plus petit (80 × 10^4 contre 3 × 10^5).
// Tout par construction, aucun flottant : la mantisse décalée est valOf(c, ±d).
const paireTrompeuse = (max = null) => {
    const a = max ? max.m : mantAlea();
    const p = max ? max.e : randInt(TROMPEUR_P_MIN, TROMPEUR_P_MAX);
    // Le voisin : au même exposant seulement si une mantisse plus petite existe
    // (a > 1) ; tirage borné de c < a, repli c = 1.
    let t = p - 1;
    let c = mantAlea();
    if (a !== '1' && randomChoice([false, true])) {
        t = p;
        c = '1';
        for (let essai = 0; essai < 40; essai++) {
            const x = mantAlea();
            if (cmpDec(x, a) < 0) { c = x; break; }
        }
    }
    // Le décalage doit dépasser l'écart d'exposant (p - t) pour que l'exposant
    // affiché passe de l'autre côté : au même exposant, 1 ou 2 crans ; un cran
    // en dessous, 2 crans. Repli si la liste des amplitudes ne le permet pas.
    const admis = TROMPEUR_DECALAGES.filter(x => x > p - t);
    const d = admis.length ? randomChoice(admis) : p - t + 1;
    if (randomChoice([false, true])) {
        return { grand: { m: a, e: p }, petit: { m: valOf(c, -d), e: t + d }, sens: 'haut' };
    }
    return { grand: { m: valOf(a, d), e: p - d }, petit: { m: c, e: t }, sens: 'bas' };
};

// Marqueur de différenciation, posé seulement sur les tirages difficiles :
// contenu.difficulte survit à l'enregistrement en base (contenu est persisté,
// _meta est effacé au POST, seul generatorKey en survit) ; recopié dans
// _meta.params, avec le sens du piège, pour le harnais et les dumps.
const marquerDifficile = (payload, sens) => {
    if (!sens) return payload;
    payload.contenu = { ...payload.contenu, difficulte: DIFFICULTE_TROMPEUR };
    payload._meta.params = { ...payload._meta.params, difficulte: DIFFICULTE_TROMPEUR, sens };
    return payload;
};

// ═════════ Carte « comparer » — Compare deux nombres (<, >, =) ═════════

const genComparerQcm = () => {
    let m1; let e1; let m2; let e2; let sens = null;
    if (tirageTrompeur()) {
        // Variante difficile : A reste normalisé et B porte l'écriture décalée,
        // comme hors variante ; le sens dit si B est le plus petit (« haut »,
        // réponse >) ou le plus grand (« bas », réponse <).
        const paire = paireTrompeuse();
        sens = paire.sens;
        const [A, B] = sens === 'haut' ? [paire.grand, paire.petit] : [paire.petit, paire.grand];
        m1 = A.m; e1 = A.e; m2 = B.m; e2 = B.e;
    } else {
        m1 = mantAlea();
        e1 = randInt(-4, 6);
        if (randInt(1, 4) === 1) {
            // ÉGALITÉ : même valeur, écriture décalée (m1 × 10^e1 = m2 × 10^e2).
            [m2, e2] = decaler(m1, e1);
        } else {
            // Valeur DIFFÉRENTE (tirage borné, repli déterministe B = 10 × A), puis
            // le même décalage d'écriture une fois sur deux : la forme de B ne
            // trahit plus la réponse (avant, seule l'égalité décalait B).
            m2 = m1; e2 = e1 + 1;
            for (let essai = 0; essai < 60; essai++) {
                const m = mantAlea(); const e = randInt(-4, 6);
                if (cmpDec(valOf(m1, e1), valOf(m, e)) !== 0) { m2 = m; e2 = e; break; }
            }
            if (randomChoice([false, true])) [m2, e2] = decaler(m2, e2);
        }
    }
    const c = cmpDec(valOf(m1, e1), valOf(m2, e2));
    const correct = c < 0 ? '<' : (c > 0 ? '>' : '=');
    const texte = `<p>On considère les nombres ${tex(`A = ${sciFrag(m1, e1)}`)} et ${tex(`B = ${sciFrag(m2, e2)}`)}.</p>`
        + '<p><b>Complète : A … B.</b> Choisis le symbole qui convient.</p>';
    const payload = makeQcm(['<', '>', '='], correct, texte, opts(L_COMPARER));
    payload._meta.params = { m1, e1, m2, e2, correct };
    return marquerDifficile(payload, sens);
};

// ═════════ Jeux de k nombres (plusgrand, ranger) ═════════

const valeursDistinctes = (nums) => {
    const vals = nums.map(n => valOf(n.m, n.e));
    return vals.every((v, i) => vals.every((w, j) => i === j || cmpDec(v, w) !== 0));
};

const indexMax = (nums) => {
    let idx = 0;
    for (let i = 1; i < nums.length; i++) {
        if (cmpDec(valOf(nums[i].m, nums[i].e), valOf(nums[idx].m, nums[idx].e)) > 0) idx = i;
    }
    return idx;
};

// k nombres normalisés à valeurs deux à deux distinctes (tirage borné, repli
// déterministe 1 × 10^0, 2 × 10^1, 3 × 10^2, 4 × 10^3).
const tirerDistincts = (k) => {
    for (let essai = 0; essai < 80; essai++) {
        const nums = [];
        for (let i = 0; i < k; i++) nums.push({ m: mantAlea(), e: randInt(-3, 6) });
        if (valeursDistinctes(nums)) return nums;
    }
    return Array.from({ length: k }, (_, i) => ({ m: String(i + 1), e: i }));
};

// Hors variante : une écriture non normalisée une fois sur deux, sur un nombre
// au hasard, le plus grand compris. Seule l'écriture change, jamais la valeur :
// la forme d'un nombre ne dit rien de son rôle.
const decalerUnAuHasard = (nums) => {
    if (randomChoice([false, true])) return nums;
    const i = randInt(0, nums.length - 1);
    const [m, e] = decaler(nums[i].m, nums[i].e);
    return nums.map((n, j) => (j === i ? { m, e } : n));
};

// Jeu difficile de k nombres : tirage ordinaire, puis un nombre autre que le
// plus grand est remplacé par le voisin piégé du plus grand (paireTrompeuse) :
// le plus grand exposant affiché n'est plus celui du plus grand nombre, dans
// les deux sens. Le voisin doit rester distinct des autres valeurs : tirage
// borné, repli déterministe (les autres nombres deux crans d'exposant en
// dessous, mantisses 1 à 4 : distincts entre eux, du plus grand et du voisin).
const jeuTrompeur = (k) => {
    const tires = tirerDistincts(k);
    const idxMax = indexMax(tires);
    const idxVoisin = (idxMax + randInt(1, k - 1)) % k;
    const max = tires[idxMax];
    const estAutre = (i) => i !== idxMax && i !== idxVoisin;
    let paire = null;
    for (let essai = 0; essai < 40 && !paire; essai++) {
        const cand = paireTrompeuse(max);
        const v = valOf(cand.petit.m, cand.petit.e);
        if (tires.every((n, i) => !estAutre(i) || cmpDec(valOf(n.m, n.e), v) !== 0)) paire = cand;
    }
    const autres = paire ? tires : tires.map((n, i) => (estAutre(i) ? { m: String(i + 1), e: max.e - 2 } : n));
    if (!paire) paire = paireTrompeuse(max);
    const nums = autres.map((n, i) => (i === idxMax ? paire.grand : (i === idxVoisin ? paire.petit : n)));
    return { nums, sens: paire.sens };
};

// ═════════ Carte « plusgrand » — Le plus grand (QCM) ═════════

const genPlusGrandQcm = () => {
    const k = randInt(3, 4);
    let nums; let sens = null;
    if (tirageTrompeur()) ({ nums, sens } = jeuTrompeur(k));
    else nums = decalerUnAuHasard(tirerDistincts(k));
    const idxMax = indexMax(nums);
    const choix = nums.map(n => sci(n.m, n.e));
    const correct = choix[idxMax];
    const payload = makeQcm(choix, correct,
        '<p><b>Quel est le plus grand de ces nombres ?</b></p>', opts(L_PLUSGRAND));
    payload._meta.params = { nums, idxMax };
    return marquerDifficile(payload, sens);
};

// ═════════ Carte « ranger » — Ordre croissant (ordonner) ═════════

const genRangerOrdonner = () => {
    const k = 4;
    let nums; let sens = null;
    if (tirageTrompeur()) ({ nums, sens } = jeuTrompeur(k));
    else nums = decalerUnAuHasard(tirerDistincts(k));
    const ordre = [...nums]
        .sort((a, b) => cmpDec(valOf(a.m, a.e), valOf(b.m, b.e)))
        .map(n => sci(n.m, n.e));
    const payload = makeOrdonner(ordre,
        '<p><b>Range ces nombres dans l\'ordre croissant, du plus petit au plus grand.</b></p>',
        opts(L_RANGER));
    payload._meta.params = { ordre };
    return marquerDifficile(payload, sens);
};

// ═════════ Carte « encadrer » — Deux puissances de 10 consécutives (saisie) ═════════

// Mantisse strictement entre 1 et 10, de 0 à 4 décimales, jamais « 1 » seul
// (N ne doit pas être une puissance de 10) : repli déterministe, sans boucle.
const mantEncadrer = () => {
    let ent = randInt(1, 9);
    const nd = randInt(0, 4);
    let d = '';
    for (let i = 0; i < nd; i++) d += String(randInt(0, 9));
    d = d.replace(/0+$/, '');
    if (!d && ent === 1) ent = randInt(2, 9);
    return d ? `${ent},${d}` : String(ent);
};

const genEncadrerTrous = () => {
    const p = randInt(-4, 6);
    const mant = mantEncadrer();
    const brutN = valOf(mant, p);
    const N = grp(brutN);
    // Ligne entière en mode mathématique (dollars aux deux bouts, cf.
    // hasMathLines) : chaque exposant en trou est protégé par un groupe,
    // 10^{{{p}}} et jamais 10^{{p}} (KaTeX refuse un ^ suivi du markup du trou).
    const phrase = `$10^{{{${p}}}} < ${nbTex(brutN)} < 10^{{{${p + 1}}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut encadrer ${N} entre deux puissances de 10 consécutives.</p>`
        + `<p><b>Complète les deux exposants</b> : ${tex(`10^{\\ldots} \\lt ${nbTex(brutN)} \\lt 10^{\\ldots}`)}.</p>`
        + '<p>Un exposant négatif s\'écrit avec le signe « - », par exemple -3.</p>',
        { ...opts(L_ENCADRER), mode: 'saisie' });
    payload._meta.params = { p, mant, N };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    comparer_qcm_unique: genComparerQcm,
    plusgrand_qcm_unique: genPlusGrandQcm,
    ranger_ordonner: genRangerOrdonner,
    encadrer_texte_trou_saisie: genEncadrerTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', ordonner: 'Ranger', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'comparer', label: L_COMPARER, formatsList: buildFormats('comparer') },
    { id: 'plusgrand', label: L_PLUSGRAND, formatsList: buildFormats('plusgrand') },
    { id: 'ranger', label: L_RANGER, formatsList: buildFormats('ranger') },
    { id: 'encadrer', label: L_ENCADRER, formatsList: buildFormats('encadrer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
